// Усиление входа по замеру микрофона.
//
// Вся арифметика здесь и в отрыве от DOM — ровно затем, чтобы её можно было
// проверить тестом. Нужна она потому, что пороги (гейт, детектор речи, порог
// значимости RMVPE) сравниваются с уровнем сигнала, а уровень у браузера
// заметно ниже, чем у нативного захвата с той же карты: подкручивать пришлось
// бы каждый порог по отдельности, в четырёх разных шкалах, вместо одного
// числа здесь.

import type { MeterReading } from "./meter.ts";

/**
 * Уровень речи, на который рассчитаны умолчания порогов.
 *
 * Умолчание энергетического детектора — 0.02, и это «уже не тишина», а не
 * «нормальная речь». Цель ставим заметно выше порога, чтобы речь его
 * уверенно перешагивала, но не у самой единицы: между ними должен остаться
 * запас на тихий слог.
 */
export const TARGET_RMS = 0.05;

/**
 * Потолок пика после усиления.
 *
 * Не 1.0: замер длится восемь секунд, а говорить будут дольше и громче.
 * Клиппинг на входе модель превращает в хрип, и это необратимо — в отличие от
 * недостающих децибел.
 */
export const PEAK_CEILING = 0.7;

const db = (ratio: number): number => 20 * Math.log10(ratio);

/**
 * Сколько децибел добавить входу по результату замера.
 *
 * `null` означает «советовать нечего»: либо микрофон не услышал ничего, либо
 * уровень уже там, где надо. Ноль как совет выглядел бы как «мы посчитали и
 * решили ничего не менять», а это то же самое молчание, только громкое.
 */
export function recommendGainDb(
  reading: MeterReading,
  min: number,
  max: number,
): number | null {
  if (!(reading.rms > 0)) {
    return null;
  }

  // Берём меньшее из двух: сколько нужно по среднему уровню и сколько
  // позволяет пик. Иначе тихий голос с редкими всплесками уедет в клиппинг.
  const wanted = db(TARGET_RMS / reading.rms);
  const headroom = reading.peak > 0 ? db(PEAK_CEILING / reading.peak) : wanted;

  const value = Math.round(Math.min(wanted, headroom));
  const clamped = Math.min(max, Math.max(min, value));
  return clamped === 0 ? null : clamped;
}
